// src/modules/app-views/goodsReceipt/pages/Document/decorators/request.js
// *******************************************************************************
// REQUEST DECORATOR
// *******************************************************************************
// Transforms goods receipt data before sending to the backend API.
// Ensures proper data structure for items, payment, and fiscal data.
// *******************************************************************************

/**
 * Decorate goods receipt request data before sending to API
 * @param {Object} ctx - Context object containing data and form values
 * @returns {Object} Transformed data
 */
const goodsReceiptDocumentDecorateRequest = ctx => {
  const { data } = ctx;

  if (!data) return data;

  const transformed = { ...data };

  // Ensure items array has sequential itemNumber values
  if (transformed.items && Array.isArray(transformed.items)) {
    transformed.items = transformed.items.map((item, index) => ({
      ...item,
      itemNumber: index + 1,
    }));
  }

  // Ensure payment methods amounts are numbers
  if (transformed.payment?.methods && Array.isArray(transformed.payment.methods)) {
    transformed.payment.methods = transformed.payment.methods.map(method => ({
      ...method,
      amount: Number(method.amount) || 0,
    }));
  }

  // Ensure totals fields are numbers
  if (transformed.totals) {
    const totalFields = [
      'productsAmount',
      'servicesAmount',
      'discountAmount',
      'freightAmount',
      'insuranceAmount',
      'otherExpensesAmount',
      'icmsBaseAmount',
      'icmsAmount',
      'pisAmount',
      'cofinsAmount',
      'ipiAmount',
      'documentAmount',
    ];

    totalFields.forEach(field => {
      if (transformed.totals[field] !== undefined) {
        transformed.totals[field] = Number(transformed.totals[field]) || 0;
      }
    });
  }

  // Rule: NFC-e is always a national operation
  if (transformed.documentType === 'nfce') {
    transformed.operationType = 'national';
  }

  // Clean up undefined/null values from issuer snapshot
  if (transformed.issuer) {
    transformed.issuer = cleanUndefined(transformed.issuer);
  }

  // Clean up undefined/null values from recipient snapshot
  if (transformed.recipient) {
    transformed.recipient = cleanUndefined(transformed.recipient);
  }

  // Clean up carrier snapshot under logistics
  if (transformed.logistics?.carrier) {
    transformed.logistics.carrier = cleanUndefined(transformed.logistics.carrier);
  }

  // Ensure logistics.freightModeCode is a string
  if (transformed.logistics?.freightModeCode !== undefined) {
    transformed.logistics.freightModeCode = String(transformed.logistics.freightModeCode);
  }

  // Ensure logistics.volumes weights are numbers
  if (transformed.logistics?.volumes) {
    const v = transformed.logistics.volumes;
    if (v.quantity !== undefined) v.quantity = Number(v.quantity) || 0;
    if (v.grossWeight !== undefined) v.grossWeight = Number(v.grossWeight) || 0;
    if (v.netWeight !== undefined) v.netWeight = Number(v.netWeight) || 0;
  }

  // Ensure billing amounts are numbers
  if (transformed.payment?.billing) {
    const b = transformed.payment.billing;
    if (b.originalAmount !== undefined) b.originalAmount = Number(b.originalAmount) || 0;
    if (b.discountAmount !== undefined) b.discountAmount = Number(b.discountAmount) || 0;
    if (b.netAmount !== undefined) b.netAmount = Number(b.netAmount) || 0;
  }

  // Ensure installment amounts are numbers and dates are valid
  if (transformed.payment?.installments && Array.isArray(transformed.payment.installments)) {
    transformed.payment.installments = transformed.payment.installments.map(inst => ({
      ...inst,
      amount: Number(inst.amount) || 0,
    }));
  }

  // Remove carrierId (form-level field, not persisted at top level)
  delete transformed.carrierId;

  return transformed;
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default goodsReceiptDocumentDecorateRequest;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Remove undefined and null values from a shallow object
 * @param {Object} obj - Object to clean
 * @returns {Object} Object without undefined/null values
 */
function cleanUndefined(obj) {
  const cleaned = {};
  Object.keys(obj).forEach(key => {
    if (obj[key] !== undefined && obj[key] !== null) {
      cleaned[key] = obj[key];
    }
  });
  return cleaned;
}
